"use client";
import { useEffect, useState } from "react";
import {
  Search,
  User,
  Filter,
  Trash2,
  Plus,
  Grid,
  List,
  Edit,
  X,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  ArrowUp,
  ArrowDown,
  Info,
} from "lucide-react";
import Modal from "../layout/Modal";
import "./UserSettingsPage.css";
import {
  formatEmployeeCreatedAt,
  handleApiError as baseHandleApiError,
} from "../shared/util";
import SubscriptionExpiredModal from "../dashboard/SubscriptionExpiredModal";
import { useNavigate } from "react-router-dom";
import BackButton from "../shared/backButton";
import EmptyState from "../shared/EmptyState.js";
import FilterNoResults from "../shared/FilterNoResults.js";
import Pagination from "../shared/paginator/paginator";
import { useTranslation } from "react-i18next";
import {
  getConsoleUsersListApi,
  updateConsoleUsersDataApi,
  addConsoleUserApi,
  getDepartmentsListApi,
  deleteConsoleUserApi,
} from "../../services/settingsService";
import MessageManager from "../layout/Message";
import {
  getUserManagementAccess,
  getAssignableRoleOptions,
  canEditRoleDropdown,
  getSingleRoleValue,
  canDeleteUser,
  getLoggedInUserRole,
  ROLES,
} from "../shared/access";
import { getPartnersListApi } from "../../services/hiringService";

const extractPartnersList = (response) =>
  [
    response,
    response?.data,
    response?.data?.data,
    response?.result,
    response?.data?.result,
    response?.partners,
    response?.data?.partners,
    response?.items,
    response?.data?.items,
  ].find(Array.isArray) || [];

const normalizePartnerOptions = (partners = []) =>
  partners
    .filter(
      (partner) =>
        String(partner?.status || "")
          .trim()
          .toUpperCase() === "ACTIVE",
    )
    .map((partner, index) => {
      const id =
        partner?._id ||
        partner?.id ||
        partner?.partnerId ||
        partner?.partnerTenantId ||
        partner?.tenantId ||
        "";
      const name =
        partner?.partnerOrganization ||
        partner?.organizationName ||
        partner?.partnerName ||
        partner?.name ||
        "";
      const orgCode =
        partner?.partnerOrgCode ||
        partner?.organizationCode ||
        partner?.orgCode ||
        "";
      const value = String(id || orgCode || name || `partner-${index}`);
      const label = orgCode ? `${name || "Partner"} (${orgCode})` : name;

      return {
        id: value,
        value,
        label,
        name,
        orgCode,
      };
    })
    .filter((partner) => partner.label)
    .filter(
      (partner, index, list) =>
        list.findIndex((item) => item.value === partner.value) === index,
    )
    .sort((first, second) => first.label.localeCompare(second.label));

const resolvePartnerSelectionValue = (storedPartner, partnerOptions) => {
  if (!storedPartner) return "";

  const normalizedStoredPartner = String(storedPartner).trim().toLowerCase();
  const matchedPartner = partnerOptions.find((partner) =>
    [partner.value, partner.id, partner.name, partner.orgCode]
      .filter(Boolean)
      .some(
        (partnerValue) =>
          String(partnerValue).trim().toLowerCase() === normalizedStoredPartner,
      ),
  );

  return matchedPartner?.value || storedPartner;
};

const SkeletonListRow = () => {
  return (
    <div className="list-row skeleton-list-row">
      <div className="list-col">
        <div className="employee-name-cell">
          <div className="employee-avatar-small skeleton-avatar" />
          <div className="skeleton-line short" />
        </div>
      </div>
      <div className="list-col">
        <div className="skeleton-line" />
      </div>
      <div className="list-col">
        <div className="skeleton-badge" />
      </div>
      <div className="list-col">
        <div className="skeleton-line" />
      </div>
      <div className="list-col">
        <div className="skeleton-icon-btn" />
      </div>
    </div>
  );
};

const SkeletonCard = () => {
  return (
    <div className="employee-card skeleton-card">
      <div className="card-header">
        <div className="employee-avatar skeleton-avatar" />
        <div className="skeleton-line short" />
        <div className="skeleton-line" />
      </div>
      <div className="employee-details">
        <div className="skeleton-line" />
        <div className="skeleton-line" />
      </div>
    </div>
  );
};

const UserSettingsPage = () => {
  const { t } = useTranslation();
  const navigator = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("All");
  const [filterDepartment, setFilterDepartment] = useState("All");
  const [sortBy, setSortBy] = useState("Name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [tableSortBy, setTableSortBy] = useState(null);
  const [tableSortOrder, setTableSortOrder] = useState("asc");
  const [showModal, setShowModal] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
    userId: null,
  });
  // const [usersData, setUsersData] = useState([]);
  const userMangmentAccess = getUserManagementAccess();
  const loggedInRole = getLoggedInUserRole();
  const disabledPermissionTooltip =
    loggedInRole === ROLES.ADMIN
      ? t("common.permissions.contactSuperAdminForAction")
      : loggedInRole === ROLES.MANAGER || loggedInRole === ROLES.GUEST
        ? t("common.permissions.contactAdminForAction")
        : "";
  // PAGINATION LOGIC
  const [usersLength, setUsersLength] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // ADD/EDIT USER MODAL
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const defaultGuestAccess = {
    guestAccessType: "standard",
    partner: "",
    canEditPartnerPermissions: false,
    canViewPartnerProfiles: true,
    canViewOtherPartnerProfiles: false,
    canViewOrganizationProfiles: false,
  };

  const [partnerOptions, setPartnerOptions] = useState([]);
  const [isPartnersLoading, setIsPartnersLoading] = useState(false);
  const [partnersLoadError, setPartnersLoadError] = useState("");

  const [userForm, setUserForm] = useState({
    name: "",
    email: "",
    role: "",
    department: "",
    ...defaultGuestAccess,
  });

  const [showFilters, setShowFilters] = useState(false);

  // Smart search & filter toggles (same as Employees)
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);

  useEffect(() => {
    // Fetch users from API
    getAllUsersApiHandler();
  }, [
    currentPage,
    searchTerm,
    filterRole,
    filterDepartment,
    sortBy,
    itemsPerPage,
  ]);

  // const [departmentsList, setDepartmentsList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [userData, setUserData] = useState({});
  // Mock API call - replace with actual API
  const getAllUsersApiHandler = async () => {
    try {
      setIsLoading(true);
      const obj = {
        pageNo: currentPage,
        pageSize: itemsPerPage,
        role: filterRole,
        searchFilter: searchTerm,
        department: filterDepartment,
        sortBy: sortBy,
      };
      const response = await getConsoleUsersListApi(obj);
      console.log("API Response:", response);
      setUserData(response.data);
      // setUsers(filteredUsers);
      setUsersLength(response.totalCount);
    } catch (error) {
      console.error("Error fetching users:", error);
      handleApiError(error, { showModal: false });
    } finally {
      setIsLoading(false);
    }
  };

  const getPartnersListApiHandler = async () => {
    try {
      setIsPartnersLoading(true);
      setPartnersLoadError("");

      const response = await getPartnersListApi();
      const partnersData = extractPartnersList(response);

      setPartnerOptions(normalizePartnerOptions(partnersData));
    } catch (error) {
      console.error("Error fetching partners list:", error);
      setPartnerOptions([]);
      setPartnersLoadError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load partner organizations.",
      );
      handleApiError(error, {
        showModal: false,
      });
    } finally {
      setIsPartnersLoading(false);
    }
  };

  const handleTableSort = (field) => {
    if (tableSortBy === field) {
      setTableSortOrder((currentOrder) =>
        currentOrder === "asc" ? "desc" : "asc",
      );
      return;
    }

    setTableSortBy(field);
    setTableSortOrder("asc");
  };

  const getTableSortIcon = (field) => {
    if (tableSortBy !== field) {
      return <ChevronsUpDown size={14} className="table-sort-icon" />;
    }

    return tableSortOrder === "asc" ? (
      <ChevronUp size={14} className="table-sort-icon active" />
    ) : (
      <ChevronDown size={14} className="table-sort-icon active" />
    );
  };

  const getUserTableSortValue = (user, field) => {
    switch (field) {
      case "name":
        return user.name || "";
      case "email":
        return user.email || "";
      case "role":
        return Array.isArray(user.role)
          ? user.role.join(", ")
          : user.role || "";
      case "department":
        return user.department || "";
      case "updatedAt":
        return user.updatedAt ? new Date(user.updatedAt).getTime() : null;
      default:
        return "";
    }
  };

  const compareUserTableValues = (aValue, bValue) => {
    const aIsEmpty = aValue === null || aValue === undefined || aValue === "";
    const bIsEmpty = bValue === null || bValue === undefined || bValue === "";

    if (aIsEmpty && bIsEmpty) return 0;
    if (aIsEmpty) return 1;
    if (bIsEmpty) return -1;

    const comparison =
      typeof aValue === "number" && typeof bValue === "number"
        ? aValue - bValue
        : String(aValue).localeCompare(String(bValue), undefined, {
            numeric: true,
            sensitivity: "base",
          });

    return tableSortOrder === "asc" ? comparison : -comparison;
  };

  const sortedTableUsers = tableSortBy
    ? [...(Array.isArray(userData) ? userData : [])].sort((a, b) =>
        compareUserTableValues(
          getUserTableSortValue(a, tableSortBy),
          getUserTableSortValue(b, tableSortBy),
        ),
      )
    : Array.isArray(userData)
      ? userData
      : [];

  const [viewMode, setViewMode] = useState("list");
  const canDeleteSelectedUser = (user) => {
    const targetRole = getSingleRoleValue(user?.role);
    return canDeleteUser(targetRole);
  };

  const getDeleteDisabledTooltip = (user) => {
    const targetRole = String(getSingleRoleValue(user?.role) || "")
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "_");

    if (targetRole === ROLES.SUPER_ADMIN) {
      return t("common.permissions.submitSupportFormForAssistance");
    }

    if (targetRole === ROLES.BRAND_MANAGER) {
      return t("common.permissions.contactSuperAdminForAction");
    }

    if (targetRole === ROLES.ADMIN) {
      return t("common.permissions.contactSuperAdminForAction");
    }

    return disabledPermissionTooltip || t("userSettings.cannotDeleteUser");
  };
  // const getEditDisabledTooltip = (user) => {
  //   const targetRole = String(getSingleRoleValue(user?.role) || "")
  //     .trim()
  //     .toUpperCase()
  //     .replace(/\s+/g, "_");

  //   if (targetRole === ROLES.SUPER_ADMIN) {
  //     return t("common.permissions.submitSupportFormForAssistance");
  //   }

  //   if (targetRole === ROLES.BRAND_MANAGER) {
  //     return t("common.permissions.contactSuperAdminForAction");
  //   }

  //   if (
  //     loggedInRole !== ROLES.SUPER_ADMIN &&
  //     (targetRole === ROLES.ADMIN || targetRole === ROLES.SUPER_ADMIN)
  //   ) {
  //     return t("common.permissions.contactSuperAdminForAction");
  //   }

  //   return disabledPermissionTooltip || t("userSettings.editUser");
  // };
  const getEditDisabledTooltip = (user) => {
    const targetRole = String(getSingleRoleValue(user?.role) || "")
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "_");

    if (targetRole === ROLES.SUPER_ADMIN) {
      return t("common.permissions.submitSupportFormForAssistance");
    }

    if (targetRole === ROLES.BRAND_MANAGER) {
      return t("common.permissions.contactSuperAdminForAction");
    }

    if (
      loggedInRole !== ROLES.SUPER_ADMIN &&
      (targetRole === ROLES.ADMIN || targetRole === ROLES.SUPER_ADMIN)
    ) {
      return t("common.permissions.contactSuperAdminForAction");
    }

    return disabledPermissionTooltip || t("userSettings.editUser");
  };
  const canEditSelectedUser = (user) => {
    const targetRole = getSingleRoleValue(user?.role);
    return userMangmentAccess.canEditUsers && canEditRoleDropdown(targetRole);
  };
  const roles = [
    t("common.all"),
    t("userSettings.roles.admin"),
    t("userSettings.roles.manager"),
    t("userSettings.roles.guest"),
  ];
  const roleOptions = [
    { value: "Admin", label: t("userSettings.roles.admin") },
    { value: "Manager", label: t("userSettings.roles.manager") },
    { value: "Guest", label: t("userSettings.roles.guest") },
  ];

  const roleLegendItems = [
    {
      title: t("userSettings.roleGuide.superAdminTitle"),
      description: t("userSettings.roleGuide.superAdminDescription"),
    },
    {
      title: t("userSettings.roleGuide.adminTitle"),
      description: t("userSettings.roleGuide.adminDescription"),
    },
    {
      title: t("userSettings.roleGuide.managerTitle"),
      description: t("userSettings.roleGuide.managerDescription"),
    },
    {
      title: t("userSettings.roleGuide.brandManagerTitle"),
      description: t("userSettings.roleGuide.brandManagerDescription"),
    },
    {
      title: t("userSettings.roleGuide.guestTitle"),
      description: t("userSettings.roleGuide.guestDescription"),
    },
  ];
  const assignableRoleOptions = getAssignableRoleOptions(roleOptions);

  const canEditSelectedUserRole = editingUser
    ? canEditRoleDropdown(editingUser.role)
    : true;

  const roleDropdownOptions =
    editingUser && !canEditSelectedUserRole
      ? [
          {
            value: getSingleRoleValue(userForm.role),
            label: getSingleRoleValue(userForm.role),
            disabled: false,
          },
        ]
      : assignableRoleOptions;
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  const sortOptions = [
    { value: "Name", label: t("userSettings.listView.name") },
    { value: "Email", label: t("userSettings.listView.email") },
    { value: "Role", label: t("userSettings.listView.role") },
    { value: "Department", label: t("userSettings.listView.department") },
    {
      value: "Date Ascending",
      label: t("employees.listView.updatedAt"),
      icon: <ArrowUp size={12} />,
    },
    {
      value: "Date Descending",
      label: t("employees.listView.updatedAt"),
      icon: <ArrowDown size={12} />,
    },
  ];
  const selectedSortOption =
    sortOptions.find((option) => option.value === sortBy) || sortOptions[0];
  const getRoleColor = (role) => {
    switch (role) {
      case "admin":
        return "user-role-admin";
      case "super-admin":
        return "user-role-admin";
      case "manager":
        return "user-role-manager";
      case "recruiter":
        return "user-role-recruiter";
      default:
        return "user-role-guest";
    }
  };

  const [showSubscriptionExpired, setShowSubscriptionExpired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState("");

  const navigate = useNavigate();
  const onNavigate = (page) => {
    console.log("Navigating to:", page);
    navigate(page);
  };

  const handleSubscriptionRedirect = () =>
    new Promise((resolve) => {
      setTimeout(() => {
        navigate("/pricing");
        resolve();
      }, 100);
    });

  const handleApiError = (error, options) => {
    if (error?.response?.status === 403) {
      setSubscriptionMessage(
        error?.response?.data?.message || t("subscription.upgradePlanMessage"),
      );
      setShowSubscriptionExpired(true);
      return;
    }
    return baseHandleApiError(error, options);
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setUserForm({
      name: "",
      email: "",
      role: "",
      department: "",
      ...defaultGuestAccess,
    });
    setShowUserModal(true);
  };

  const handleEditUser = (user) => {
    console.log("Editing user:", user);

    const selectedRole = getSingleRoleValue(user.role);

    setEditingUser(user);
    setUserForm({
      name: user.name,
      email: user.email,
      role: selectedRole,
      department: user.department,
      guestAccessType:
        user.guestAccessType || defaultGuestAccess.guestAccessType,
      partner: user.partner || defaultGuestAccess.partner,
      canEditPartnerPermissions:
        user.canEditPartnerPermissions ??
        defaultGuestAccess.canEditPartnerPermissions,
      canViewPartnerProfiles:
        user.canViewPartnerProfiles ??
        defaultGuestAccess.canViewPartnerProfiles,
      canViewOtherPartnerProfiles:
        user.canViewOtherPartnerProfiles ??
        defaultGuestAccess.canViewOtherPartnerProfiles,
      canViewOrganizationProfiles:
        user.canViewOrganizationProfiles ??
        defaultGuestAccess.canViewOrganizationProfiles,
    });
    setShowUserModal(true);
  };

  const handleDeleteUser = async (userId) => {
    console.log("Deleting user with ID:", userId);
    // const user = users.find((u) => u.id === userId);
    setShowModal({
      show: true,
      type: "warning",
      title: t("userSettings.delete.removeUser"),
      // in traslate,js use {name} not {{name}}
      message: t("userSettings.delete.confirmDelete", { name: userId.name }),
      userId: userId,
      showButtons: true,
      action: async () => {
        try {
          const response = await deleteConsoleUserApi(userId._id);
          console.log("Delete Response:", response);
          if (response.code === 200) {
            getAllUsersApiHandler();

            setShowModal({
              show: true,
              type: "success",
              title: t("common.success"),
              message: t("common.deleteUserSuccess", { name: userId.name }),
              showButton: false,
            });
          }
        } catch (error) {
          console.error("Error deleting user:", error);
          handleApiError(error, { showModal: true, showButton: true });
        }
      },
    });

    // try{
    //   const response = await deleteConsoleUserApi(userId);

    //   if(response.code === 200) {
    //     setShowModal({
    //       show: true,
    //       type: "success",
    //       title: t("userSettings.delete.userDeleted"),
    //       message: t("userSettings.delete.userDeletedMessage", { name: response.data.name }),
    //       showButton: false,
    //     });
    //     getAllUsersApiHandler();
    //   }

    // }catch(error){
    //   // console.error("Error deleting user:", error);
    //   handleApiError(error, { showModal: true, showButton: true , sh});
    // }
  };

  const confirmDelete = () => {
    // if (showModal.userId) {
    //   setUsers((prev) => prev.filter((user) => user.id !== showModal.userId));
    //   setShowModal({
    //     show: false,
    //     type: "",
    //     title: "",
    //     message: "",
    //     userId: null,
    //   });
    // }
  };

  const getAppliedFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (filterRole && filterRole !== "All") count++;
    if (filterDepartment && filterDepartment !== "All") count++;
    if (sortBy && sortBy !== "Name") count++;
    return count;
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setFilterRole("All");
    setFilterDepartment("All");
    setSortBy("Name");
    setSortOrder("asc");
    setTableSortBy(null);
    setTableSortOrder("asc");
    setShowSearchBar(false);
    setShowFiltersPanel(false);
    setCurrentPage(1);
  };

  const handleUserFormSubmit = async (e) => {
    e.preventDefault();

    const selectedRole = getSingleRoleValue(userForm.role);
    const isGuestRole = String(selectedRole || "").toLowerCase() === "guest";

    if (
      userForm.role === "Guest" &&
      userForm.guestAccessType === "partner" &&
      !userForm.partner
    ) {
      MessageManager.showToast(
        "error",
        "Please select a partner organization.",
      );
      return;
    }

    if (editingUser) {
      try {
        const obj = {
          consoleUserId: editingUser._id,
          name: userForm.name,
          role: [selectedRole],
          department: isGuestRole ? "" : userForm.department || "",
          isPartnerAssociated:
            userForm.role === "Guest" && userForm.guestAccessType === "partner",
          partner:
            userForm.role === "Guest" && userForm.guestAccessType === "partner"
              ? userForm.partner
              : "",
          canViewPartnerProfiles:
            userForm.role === "Guest" && userForm.guestAccessType === "partner",
          canViewOtherPartnerProfiles: false,
          canViewOrganizationProfiles: false,
        };

        if (!obj.name || !obj.role?.[0] || (!isGuestRole && !obj.department)) {
          setShowModal({
            show: true,
            type: "error",
            title: t("common.error"),
            message: "Please fill all required fields.",
            showButton: false,
          });
          return;
        }

        const response = await updateConsoleUsersDataApi(obj);
        if (response.code == 200) {
          setShowModal({
            show: true,
            type: "success",
            title: t("common.success"),
            message: response.message,
            showButton: false,
          });
          getAllUsersApiHandler();
        }
      } catch (error) {
        console.error("Error updating user:", error);
        handleApiError(error, { setShowModal: true, showButton: false });
      }
    } else {
      try {
        const obj = {
          name: userForm.name,
          email: userForm.email,
          role: [selectedRole],
          department: isGuestRole ? "" : userForm.department,
          isPartnerAssociated:
            userForm.role === "Guest" && userForm.guestAccessType === "partner",
          partner:
            userForm.role === "Guest" && userForm.guestAccessType === "partner"
              ? userForm.partner
              : "",
          canViewPartnerProfiles:
            userForm.role === "Guest" && userForm.guestAccessType === "partner",
          canViewOtherPartnerProfiles: false,
          canViewOrganizationProfiles: false,
        };

        if (
          !obj.name ||
          !obj.email ||
          !obj.role?.[0] ||
          (!isGuestRole && !obj.department)
        ) {
          setShowModal({
            show: true,
            type: "error",
            title: t("common.error"),
            message: t("userSettings.messages.fillRequiredFields"),
            showButton: false,
          });
          return;
        }

        const response = await addConsoleUserApi(obj);
        if (response.code === 200) {
          MessageManager.showToast("success", t("userSettings.addUserSuccess"));
          getAllUsersApiHandler();
        }
      } catch (error) {
        console.error("Error adding user:", error);
        handleApiError(error, { showModal: true, showButton: false });
      }
    }

    setShowUserModal(false);
    setUserForm({
      name: "",
      email: "",
      role: "",
      department: "",
      ...defaultGuestAccess,
    });
    setEditingUser(null);
  };
  const selectedRole = getSingleRoleValue(userForm.role);
  const isGuestRole = String(selectedRole || "").toLowerCase() === "guest";
  const sanitizeName = (value) => value.replace(/[^A-Za-z ]/g, "");

  const handleUserFormChange = (field, value) => {
    const sanitizedValue = field === "name" ? sanitizeName(value) : value;

    setUserForm((prev) => {
      const updatedForm = {
        ...prev,
        [field]: sanitizedValue,
      };

      if (
        field === "role" &&
        String(getSingleRoleValue(sanitizedValue) || "").toLowerCase() ===
          "guest"
      ) {
        updatedForm.department = "";
      }

      return updatedForm;
    });
  };

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (filterRole && filterRole !== "All") count++;
    if (filterDepartment && filterDepartment !== "All") count++;
    if (sortBy && sortBy !== "Name") count++;
    return count;
  };

  const isTrueEmptyState =
    !isLoading &&
    (userData?.length || 0) === 0 &&
    getAppliedFiltersCount() === 0;

  useEffect(() => {
    getDepartmentsListApiHandler();
    getPartnersListApiHandler();
    // getpeopleInviteLinkHandler();
  }, []);

  useEffect(() => {
    if (!showUserModal || !userForm.partner || partnerOptions.length === 0) {
      return;
    }

    const resolvedPartnerValue = resolvePartnerSelectionValue(
      userForm.partner,
      partnerOptions,
    );

    if (resolvedPartnerValue === userForm.partner) {
      return;
    }

    setUserForm((previousForm) => ({
      ...previousForm,
      partner: resolvedPartnerValue,
    }));
  }, [showUserModal, userForm.partner, partnerOptions]);

  const [departmentsList, setDepartmentsList] = useState([]);

  const getDepartmentsListApiHandler = async () => {
    try {
      const response = await getDepartmentsListApi();
      setDepartmentsList([
        { name: t("common.all"), _id: "all" },
        ...response.data,
      ]);
    } catch (error) {
      console.error("Error fetching departments list:", error);
      handleApiError(error, {
        showModal: false,
      });
    }
  };

  const sanitizeSearch = (val) =>
    // allow letters, numbers, space, dash, underscore, and dot; strip everything else (incl. backslash)
    val.replace(/[\\`~!#$%^&*()+={}[\]|:;"'<>,?/]/g, "");

  return (
    <div className="employees-page user-settings-page">
      <main className="employees-main">
        <div className="us-back-row">
          <BackButton />

          {!isTrueEmptyState && (
            <button
              disabled={!userMangmentAccess.canInviteUsers}
              className="btn btn-primary create-team-btn us-mobile-top-add-btn"
              onClick={handleAddUser}
              title={
                userMangmentAccess.canInviteUsers
                  ? t("userSettings.addUser")
                  : disabledPermissionTooltip
              }
            >
              <Plus size={16} />
              {t("userSettings.addUser")}
            </button>
          )}
        </div>
        <div className="container">
          <div className="page-header">
            <div className="header-icon">
              <User size={32} />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                justifyContent: "center",
              }}
            >
              <h1>{t("userSettings.pageTitle")}</h1>
              <span className="us-title-info-wrap">
                <button
                  type="button"
                  className="us-title-info-btn"
                  aria-label={t("userSettings.tooltips.title")}
                >
                  <Info size={18} />
                </button>

                <span className="us-title-info-popover" role="tooltip">
                  <strong>{t("userSettings.tooltips.title")}</strong>
                  <span>{t("userSettings.tooltips.message")}</span>
                </span>
              </span>
            </div>
            <p>{t("userSettings.pageDescription")}</p>

            <section
              className="us-access-key-strip"
              aria-label={t("userSettings.roleGuide.ariaLabel")}
            >
              <div className="us-access-key-label">
                <span>{t("userSettings.roleGuide.rolesLabel")}</span>
              </div>

              <div className="us-access-key-track">
                {roleLegendItems.map((item) => (
                  <div
                    className="us-access-key-item"
                    key={item.title}
                    title={`${item.title}: ${item.description}`}
                  >
                    <div className="us-access-key-item-text">
                      <span className="us-access-key-role">{item.title}</span>
                      <span className="us-access-key-separator">:</span>
                      <span className="us-access-key-description">
                        {item.description}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <div className="page-actions">
            <div className="view-toggle">
              <button
                className={`view-btn ${viewMode === "card" ? "active" : ""}`}
                onClick={() => setViewMode("card")}
                title={t("userSettings.cardView")}
              >
                <Grid size={16} />
              </button>
              <button
                className={`view-btn ${viewMode === "list" ? "active" : ""}`}
                onClick={() => setViewMode("list")}
                title={t("userSettings.listView")}
              >
                <List size={16} />
              </button>
            </div>
            {!isTrueEmptyState && (
              <button
                disabled={!userMangmentAccess.canInviteUsers}
                className="btn btn-primary create-team-btn us-desktop-add-btn"
                onClick={handleAddUser}
                title={
                  userMangmentAccess.canInviteUsers
                    ? t("userSettings.addUser")
                    : disabledPermissionTooltip
                }
              >
                <Plus size={16} />
                {t("userSettings.addUser")}
              </button>
            )}
          </div>

          {/* Smart Search & Filter panel */}
          <div className="smart-filter-panel-user">
            <div className="smart-filter-top-bar">
              {showSearchBar ? (
                <div className="smart-search-inline-row">
                  <input
                    type="text"
                    placeholder={t("userSettings.searchPlaceholder")}
                    value={searchTerm}
                    onChange={(e) =>
                      setSearchTerm(sanitizeSearch(e.target.value))
                    }
                    className="smart-search-inline-input"
                    autoFocus
                  />
                  <button
                    className="smart-filter-icon-btn active"
                    title={t("common.search")}
                    onClick={() => setShowSearchBar(false)} // close on click-back
                  >
                    <Search size={18} />
                  </button>
                  {searchTerm && (
                    <button
                      className="smart-search-clear"
                      onClick={() => setSearchTerm("")}
                      title={t("common.clear")}
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
              ) : null}

              {/* Icons pinned right */}
              <div className="smart-filter-icons-right">
                {!showSearchBar && (
                  <button
                    className={`smart-filter-icon-btn ${showSearchBar ? "active" : ""}`}
                    onClick={() => {
                      setShowSearchBar(!showSearchBar);
                      if (showSearchBar) setSearchTerm("");
                      setShowFiltersPanel(false);
                    }}
                    title={t("common.search")}
                  >
                    <Search size={18} />
                  </button>
                )}
                <div className="smart-filter-separator" />
                <button
                  className={`smart-filter-icon-btn ${showFiltersPanel ? "active" : ""}`}
                  onClick={() => {
                    setShowFiltersPanel(!showFiltersPanel);
                    if (!showFiltersPanel) setShowSearchBar(false);
                  }}
                  title={t("common.filters")}
                >
                  <Filter size={18} />
                </button>
              </div>

              <div className="smart-filter-info">
                {getAppliedFiltersCount() > 0 && (
                  <>
                    <span className="smart-filter-badge">
                      {getAppliedFiltersCount()}
                    </span>
                    <span>{t("common.filtersApplied")}</span>
                  </>
                )}
              </div>
            </div>

            {showFiltersPanel && (
              <>
                <div className="smart-filter-divider" />
                <div className="smart-filter-content">
                  <div className="smart-filter-grid">
                    <div className="smart-filter-item">
                      <label>{t("userSettings.filters.role")}:</label>
                      <select
                        value={filterRole}
                        onChange={(e) => setFilterRole(e.target.value)}
                        className={`smart-filter-select ${filterRole !== "All" ? "smart-active" : ""}`}
                      >
                        {roles.map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="smart-filter-item">
                      <label>{t("userSettings.filters.department")}:</label>
                      <select
                        value={filterDepartment}
                        onChange={(e) => {
                          if (e.target.value === "__add__") {
                            navigator("/settings/organization-info");
                            return;
                          }
                          setFilterDepartment(e.target.value);
                        }}
                        className={`smart-filter-select ${filterDepartment !== "All" ? "smart-active" : ""}`}
                      >
                        {departmentsList.length === 0 ? (
                          <>
                            <option value="">{t("common.none")}</option>
                            <option value="__add__">
                              {t("common.create")}
                            </option>
                          </>
                        ) : (
                          <>
                            <option value="">{t("common.select")}</option>
                            {departmentsList.map((dept) => (
                              <option key={dept._id} value={dept.name}>
                                {dept.name}
                              </option>
                            ))}
                          </>
                        )}
                      </select>
                    </div>

                    {/* <div className="smart-filter-item">
                      <label>{t("userSettings.filters.sortBy")}:</label>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className={`smart-filter-select ${sortBy !== "Name" ? "smart-active" : ""}`}
                      >
                        {sortOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div> */}

                    <div className="smart-filter-item">
                      <label>{t("employees.filters.sortBy")}</label>

                      <div
                        className={`smart-sort-dropdown ${
                          sortBy !== "Name" ? "smart-active" : ""
                        }`}
                        tabIndex={0}
                        onBlur={(e) => {
                          if (!e.currentTarget.contains(e.relatedTarget)) {
                            setShowSortDropdown(false);
                          }
                        }}
                      >
                        <button
                          type="button"
                          className="smart-sort-trigger"
                          onClick={() => setShowSortDropdown((prev) => !prev)}
                        >
                          <span className="smart-sort-value">
                            {selectedSortOption.icon && (
                              <span className="smart-sort-icon">
                                {selectedSortOption.icon}
                              </span>
                            )}
                            {selectedSortOption.label}
                          </span>

                          <ChevronDown
                            size={14}
                            className={`smart-sort-chevron ${
                              showSortDropdown ? "open" : ""
                            }`}
                          />
                        </button>

                        {showSortDropdown && (
                          <div className="smart-sort-menu">
                            {sortOptions.map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                className={`smart-sort-option ${
                                  sortBy === option.value ? "selected" : ""
                                }`}
                                onClick={() => {
                                  setSortBy(option.value);
                                  setTableSortBy(null);
                                  setTableSortOrder("asc");
                                  setCurrentPage(1);
                                  setShowSortDropdown(false);
                                }}
                              >
                                <span>{option.label}</span>

                                {option.icon && (
                                  <span className="smart-sort-icon">
                                    {option.icon}
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {viewMode === "card" ? (
            <div className="employees-grid">
              {isLoading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <SkeletonCard key={index} />
                ))
              ) : (userData?.length || 0) === 0 ? (
                getAppliedFiltersCount() > 0 ? (
                  <FilterNoResults
                    title={t("common.noResults")}
                    message={t("common.adjustOrClear")}
                    clearLabel={t("common.clearFilters")}
                    onClear={clearAllFilters}
                  />
                ) : (
                  <EmptyState
                    icon={User}
                    iconSize={48}
                    title={t("userSettings.emptyTitle")}
                    message={t("userSettings.emptyMessage")}
                    actionLabel={t("userSettings.addUser")}
                    onAction={handleAddUser}
                  />
                )
              ) : (
                userData.map((user) => (
                  <div key={user.id} className="employee-card">
                    <div className="card-header">
                      <div className="employee-info">
                        <div className="employee-avatar">
                          <User size={20} />
                        </div>
                        <div>
                          <h3>{user.name}</h3>
                          <div
                            className="list-col email-col"
                            title={user.email}
                          >
                            {user.email}
                          </div>
                          <p className="employee-department">
                            {user.department
                              ? user.department
                              : t("common.noData")}
                          </p>
                          <p className="employee-department">
                            {formatEmployeeCreatedAt(user.updatedAt)}
                          </p>
                        </div>
                      </div>
                      <div
                        className="card-actions"
                        style={{ marginTop: "0px" }}
                      >
                        <button
                          disabled={!canEditSelectedUser(user)}
                          className="edit-btn"
                          onClick={() => handleEditUser(user)}
                          title={
                            canEditSelectedUser(user)
                              ? t("userSettings.editUser")
                              : getEditDisabledTooltip(user)
                          }
                        >
                          <Edit size={14} />
                        </button>

                        <button
                          disabled={!canDeleteSelectedUser(user)}
                          className="delete-btn-small"
                          onClick={() => handleDeleteUser(user)}
                          title={
                            canDeleteSelectedUser(user)
                              ? t("userSettings.delete.removeUser")
                              : getDeleteDisabledTooltip(user)
                          }
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>

                    <div className="employee-details">
                      <div className="detail-row">
                        <span className={"role-badge"}>
                          {" "}
                          {/*${getRoleColor(user.role)}*/}
                          {t(`${user.role}`)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="employees-list">
              {isLoading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <SkeletonListRow key={index} />
                ))
              ) : userData.length > 0 ? (
                <>
                  <div className="u-list-header">
                    <div
                      className="list-col table-sortable"
                      onClick={() => handleTableSort("name")}
                    >
                      {t("userSettings.listView.name")}
                      {getTableSortIcon("name")}
                    </div>
                    <div
                      className="list-col table-sortable"
                      onClick={() => handleTableSort("email")}
                    >
                      {t("userSettings.listView.email")}
                      {getTableSortIcon("email")}
                    </div>
                    <div
                      className="list-col table-sortable"
                      onClick={() => handleTableSort("role")}
                    >
                      {t("userSettings.listView.role")}
                      {getTableSortIcon("role")}
                    </div>
                    <div
                      className="list-col table-sortable"
                      onClick={() => handleTableSort("department")}
                    >
                      {t("userSettings.listView.department")}
                      {getTableSortIcon("department")}
                    </div>
                    <div
                      className="list-col table-sortable"
                      onClick={() => handleTableSort("updatedAt")}
                    >
                      {t("employees.listView.updatedAt")}
                      {getTableSortIcon("updatedAt")}
                    </div>
                    <div className="list-col">
                      {t("userSettings.listView.actions")}
                    </div>
                  </div>

                  {sortedTableUsers.map((user) => (
                    <div key={user._id} className="u-list-row">
                      <div
                        className="list-col"
                        data-label={t("userSettings.listView.name")}
                      >
                        <div className="employee-name-cell">
                          <div className="employee-avatar-small">
                            <User size={16} />
                          </div>
                          <span>{user.name}</span>
                        </div>
                      </div>
                      {/* <div className="list-col">{user.email}</div> */}
                      <div
                        className="list-col email-col"
                        data-label={t("userSettings.listView.email")}
                        title={user.email}
                      >
                        {user.email}
                      </div>
                      <div
                        className="list-col"
                        data-label={t("userSettings.listView.role")}
                      >
                        <span>
                          {user.role.map((role) => (
                            <span key={role} className={`role-badge-us`}>
                              {" "}
                              {/* ${getRoleColor(role)} */}
                              {role}
                            </span>
                          ))}
                        </span>
                      </div>
                      <div className="list-col">
                        {user.department || t("common.noData")}
                      </div>
                      <div className="list-col">
                        {formatEmployeeCreatedAt(user.updatedAt) ||
                          t("common.noData")}
                      </div>
                      <div
                        className="list-col"
                        data-label={t("userSettings.listView.actions")}
                      >
                        <div className="action-buttons">
                          <button
                            disabled={!canEditSelectedUser(user)}
                            className="edit-btn-small"
                            onClick={() => handleEditUser(user)}
                            title={
                              canEditSelectedUser(user)
                                ? t("userSettings.editUser")
                                : getEditDisabledTooltip(user)
                            }
                          >
                            <Edit size={14} />
                          </button>

                          <button
                            disabled={!canDeleteSelectedUser(user)}
                            className="delete-btn-small"
                            onClick={() => handleDeleteUser(user)}
                            title={
                              canDeleteSelectedUser(user)
                                ? t("userSettings.delete.removeUser")
                                : getDeleteDisabledTooltip(user)
                            }
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              ) : getAppliedFiltersCount() > 0 ? (
                <FilterNoResults
                  title={t("common.noResults")}
                  message={t("common.adjustOrClear")}
                  clearLabel={t("common.clearFilters")}
                  onClear={clearAllFilters}
                />
              ) : (
                <EmptyState
                  icon={User}
                  iconSize={48}
                  title={t("userSettings.emptyTitle")}
                  message={t("userSettings.emptyMessage")}
                  actionLabel={t("userSettings.addUser")}
                  onAction={handleAddUser}
                />
              )}
            </div>
          )}

          {/* Pagination */}
          {usersLength > 0 && (
            <Pagination
              loading={isLoading}
              total={usersLength}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              currentPageCount={userData?.length || 0}
              onPageChange={setCurrentPage}
              pageSizeOptions={[10, 20, 40, 100]}
              onPageSizeChange={(size) => {
                setItemsPerPage(size);
                const newTotalPages = Math.max(
                  1,
                  Math.ceil((usersLength || 0) / size),
                );
                if (currentPage > newTotalPages) setCurrentPage(newTotalPages);
              }}
              infoRenderer={({ start, end, total }) =>
                `${t("userSettings.pagination.showing")} ${start} ${t(
                  "userSettings.pagination.to",
                )} ${end} ${t("userSettings.pagination.of")} ${total} ${t("userSettings.pagination.users")}`
              }
            />
          )}
        </div>
      </main>

      {/* Add/Edit User Modal */}
      {showUserModal && (
        <div className="user-modal-overlay">
          <div className="user-modal-content">
            <div className="user-modal-header">
              <h3>
                {editingUser
                  ? t("userSettings.editUser")
                  : t("userSettings.addUser")}
              </h3>
              <button
                className="user-modal-close"
                onClick={() => setShowUserModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleUserFormSubmit} className="user-form">
              <div className="user-form-group">
                <div className="us-modal-role-label-row">
                  <label>
                    {t("userSettings.form.name")}
                    <span style={{ color: "red" }}>*</span>
                  </label>

                  <div className="us-modal-role-info-wrap">
                    <button
                      type="button"
                      className="us-modal-role-info-btn"
                      aria-label={t(
                        "userSettings.roleGuide.viewRoleDescriptions",
                      )}
                    >
                      <Info size={15} />
                    </button>

                    <div className="us-modal-role-info-popover">
                      {editingUser ? (
                        <span className="us-modal-role-info-description">
                          {t("userSettings.form.nameLockedSupport")}
                        </span>
                      ) : (
                        <span className="us-modal-role-info-description">
                          {t("userSettings.form.nameInputHelp")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <input
                  type="text"
                  value={userForm.name}
                  onChange={(e) => handleUserFormChange("name", e.target.value)}
                  className="user-form-input"
                  pattern="[A-Za-z ]+"
                  readOnly={editingUser} // Make name field read-only if editing
                  title={t("userSettings.form.nameLettersOnly")}
                  required
                />
              </div>
              <div className="user-form-group">
                <label>
                  {t("userSettings.form.email")}
                  <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  disabled={editingUser} // Disable email field if editing
                  type="email"
                  value={userForm.email}
                  onChange={(e) =>
                    handleUserFormChange("email", e.target.value)
                  }
                  className="user-form-input"
                  required
                />
              </div>
              <div className="user-form-group">
                <div className="us-modal-role-label-row">
                  <label htmlFor="us-user-role-select">
                    {t("userSettings.form.role")}
                    <span style={{ color: "red" }}>*</span>
                  </label>

                  <div className="us-modal-role-info-wrap">
                    <button
                      type="button"
                      className="us-modal-role-info-btn"
                      aria-label={t(
                        "userSettings.roleGuide.viewRoleDescriptions",
                      )}
                    >
                      <Info size={15} />
                    </button>

                    <div className="us-modal-role-info-popover">
                      <div className="us-modal-role-info-title">
                        {" "}
                        {t("userSettings.roleGuide.title")}
                      </div>

                      <div className="us-modal-role-info-list">
                        {roleLegendItems.map((item) => (
                          <div
                            className="us-modal-role-info-item"
                            key={item.title}
                          >
                            <span className="us-modal-role-info-name">
                              {item.title}
                            </span>
                            <span className="us-modal-role-info-separator">
                              :
                            </span>
                            <span className="us-modal-role-info-description">
                              {item.description}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <select
                  id="us-user-role-select"
                  value={getSingleRoleValue(userForm.role)}
                  onChange={(e) => handleUserFormChange("role", e.target.value)}
                  className="user-form-select"
                  required
                  disabled={editingUser && !canEditSelectedUserRole}
                  title={
                    editingUser && !canEditSelectedUserRole
                      ? t("userSettings.form.roleChangeNotAllowed")
                      : ""
                  }
                >
                  <option value="">{t("userSettings.form.selectRole")}</option>

                  {roleDropdownOptions.map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                      disabled={
                        !editingUser || canEditSelectedUserRole
                          ? option.disabled
                          : false
                      }
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              {/* {isGuestRole && (
                <div className="guest-access-box">
                  <label>
                    Is this user associated with a partner organization?
                  </label>
                  <div className="guest-access-options">
                    <label>
                      <input
                        type="radio"
                        name="guestPartnerAssociation"
                        value="standard"
                        checked={userForm.guestAccessType === "standard"}
                        onChange={() =>
                          setUserForm({
                            ...userForm,
                            guestAccessType: "standard",
                            partner: "",
                          })
                        }
                      />
                      No
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="guestPartnerAssociation"
                        value="partner"
                        checked={userForm.guestAccessType === "partner"}
                        onChange={() =>
                          setUserForm({
                            ...userForm,
                            guestAccessType: "partner",
                          })
                        }
                      />
                      Yes
                    </label>
                  </div>

                  {userForm.guestAccessType === "partner" && (
                    <div className="user-form-group">
                      <label>Select Partner</label>
                      <select
                        className="user-form-select"
                        value={userForm.partner}
                        disabled={isPartnersLoading}
                        onChange={(e) =>
                          setUserForm({ ...userForm, partner: e.target.value })
                        }
                      >
                        <option value="">
                          {isPartnersLoading
                            ? "Loading partner organizations..."
                            : "Select partner organization"}
                        </option>
                        {partnerOptions.map((partner) => (
                          <option key={partner.value} value={partner.value}>
                            {partner.label}
                          </option>
                        ))}
                      </select>
                      {partnersLoadError && (
                        <p className="guest-access-helper">
                          {partnersLoadError}
                        </p>
                      )}
                      <p className="guest-access-helper">
                        Choose the partner organization this user is associated
                        with
                      </p>
                      <p className="guest-access-helper">
                        This user's access will follow the visibility settings
                        configured for the selected partner
                      </p>
                    </div>
                  )}
                </div>
              )} */}
              <div className="user-form-group">
                <label>
                  {t("userSettings.form.department")}
                  {!isGuestRole && <span style={{ color: "red" }}>*</span>}
                </label>

                <select
                  value={userForm.department}
                  onChange={(e) => {
                    if (e.target.value === "__add__") {
                      navigator("/settings/organization-info");
                      return;
                    }
                    handleUserFormChange("department", e.target.value);
                  }}
                  className="ufilter-select"
                  disabled={isGuestRole}
                  required={!isGuestRole}
                >
                  {departmentsList.length === 0 ? (
                    <>
                      <option value="">
                        {isGuestRole
                          ? t("userSettings.form.departmentNotRequiredForGuest")
                          : t("userSettings.form.noDepartmentsAvailable")}
                      </option>
                      {!isGuestRole && (
                        <option value="__add__">
                          {t("userSettings.form.createDepartment")}
                        </option>
                      )}
                    </>
                  ) : (
                    <>
                      <option value="">
                        {isGuestRole
                          ? t("userSettings.form.departmentNotRequiredForGuest")
                          : t("userSettings.form.selectDepartment")}
                      </option>
                      {!isGuestRole &&
                        departmentsList.slice(1).map((dept) => (
                          <option key={dept._id} value={dept.name}>
                            {dept.name}
                          </option>
                        ))}
                    </>
                  )}
                </select>
              </div>
              <div className="user-form-actions">
                <button
                  type="button"
                  className="user-form-btn user-form-btn-cancel"
                  onClick={() => setShowUserModal(false)}
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="submit"
                  className="user-form-btn user-form-btn-submit"
                >
                  {editingUser
                    ? t("userSettings.updateUser")
                    : t("userSettings.createUser")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Modal
        show={showModal.show}
        onClose={() =>
          setShowModal({
            show: false,
            type: "",
            title: "",
            message: "",
            userId: null,
          })
        }
        type={showModal.type}
        title={showModal.title}
        message={showModal.message}
        showButton={false} // Hide default buttons
      >
        {showModal.showButtons && (
          <div className="modal-actions">
            <button
              className="btn btn-outline"
              onClick={() =>
                setShowModal({
                  show: false,
                  type: "",
                  title: "",
                  message: "",
                  userId: null,
                })
              }
            >
              {t("common.cancel")}
            </button>
            <button className="btn btn-primary" onClick={showModal.action}>
              {t("userSettings.deleteBtn")}
            </button>
          </div>
        )}
      </Modal>

      <SubscriptionExpiredModal
        show={showSubscriptionExpired}
        message={subscriptionMessage}
        onRedirect={handleSubscriptionRedirect}
      />
    </div>
  );
};

export default UserSettingsPage;
